import React, { useMemo, useState, useRef, RefObject, useEffect } from 'react';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowRightIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import type { Flow, UserSession, Colaborador } from '../../types';

interface CardVencidoDetalhes {
  id: string;
  titulo: string;
  etapaNome: string;
  flowNome: string;
  dataVencimento: string;
  diasVencido?: number;
  venceHoje?: boolean;
}

interface CardMetrics {
  totalCards: number;
  cardsParticipa: number;
  cardsVencidos: number;
  cardsVencendoHoje: number;
  cardsEmDia: number;
  cardsVencendo24h: number;
  flowsParticipa: number;
  totalFlows: number;
  cardsVencidosDetalhes: CardVencidoDetalhes[];
  cardsVencendoHojeDetalhes: CardVencidoDetalhes[];
  procedimentosEnvolvido: number;
  totalProcedimentos: number;
}

interface HomeProps {
  flows: Flow[];
  session: UserSession | undefined;
  colaboradores: Colaborador[];
  onCardClick?: (flowId: string, cardId: string) => void;
  onNavigate?: (path: string) => void;
}

const Home: React.FC<HomeProps> = ({ flows, session, colaboradores, onCardClick, onNavigate }) => {
  const [showingCardsDetalhes, setShowingCardsDetalhes] = useState<{
    tipo: 'vencidos' | 'vencendoHoje';
    cards: CardVencidoDetalhes[];
  } | null>(null);
  
  const [procedimentos, setProcedimentos] = useState<any[]>([]);

  const vencidosRef = useRef<HTMLSpanElement>(null);
  const vencendoHojeRef = useRef<HTMLSpanElement>(null);

  // Identificar o colaborador atual (extraído para ser usado no avatar)
  const currentColaborador = useMemo(() => {
    if (!session?.usuario || !colaboradores) return null;
    const currentEmail = (session.usuario.email || '').toLowerCase();
    return colaboradores.find(c => (c.email || '').toLowerCase() === currentEmail)
      || colaboradores.find(c => String(c.id) === String(session.usuario.id));
  }, [session?.usuario, colaboradores]);

  // Carregar procedimentos quando o componente montar
  useEffect(() => {
    const carregarProcedimentos = async () => {
      try {
        const response = await fetch('/api/procedimentos-simples');
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setProcedimentos(data.data || []);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar procedimentos:', error);
      }
    };

    carregarProcedimentos();
  }, []);

  // Se não há sessão, mostra tela de login
  if (!session?.usuario) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm border border-slate-200">
          <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <UserIcon className="h-8 w-8 text-slate-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Bem-vindo ao Escalone</h2>
          <p className="text-slate-500 text-sm">Faça login para ver suas métricas personalizadas.</p>
        </div>
      </div>
    );
  }

  const userMetrics = useMemo<CardMetrics>(() => {
    console.log('📊 Flows recebidos:', flows?.length || 0);
    console.log('📋 Flows detalhados:', flows?.map(f => ({
      name: f.name,
      id: f.id,
      etapas: f.etapas?.length,
      cards: f.cards?.length
    })));

    flows.forEach((flow, flowIndex) => {
      console.log(`📁 Flow ${flowIndex + 1}: ${flow.name || 'Sem nome'} (ID: ${flow.id})`);
      flow.etapas?.forEach((etapa, etapaIndex) => {
        console.log(`  📋 Etapa ${etapaIndex + 1}: ${etapa.nome}`);
        console.log(`    👥 Participantes:`, etapa.participantes);
        console.log(`    🏷️ Campos:`, etapa.campos?.map(c => `${c.nome} (${c.tipo})`));
      });
      
      flow.cards?.forEach((card, cardIndex) => {
        const etapa = flow.etapas?.find(e => String(e.id) === String(card.etapaId));
        const campoVencimento = etapa?.campos?.find(campo => campo.tipo === 'data_vencimento');
        console.log(`    🃏 Card ${cardIndex + 1}: ${card.titulo}`);
        console.log(`      📍 Etapa: ${etapa?.nome}`);
        console.log(`      📅 Campo vencimento:`, campoVencimento);
        console.log(`      📊 Data vencimento:`, campoVencimento ? card.campos?.[campoVencimento.id] : 'sem campo');
      });
    });

    // Sem usuário logado
    if (!session?.usuario) {
      console.log('❌ Usuário não logado');
      return {
        totalCards: flows.flatMap(f => f.cards || []).length,
        cardsParticipa: 0,
        cardsVencidos: 0,
        cardsVencendoHoje: 0,
        cardsEmDia: 0,
        cardsVencendo24h: 0,
        flowsParticipa: 0,
        totalFlows: flows.length,
        cardsVencidosDetalhes: [],
        cardsVencendoHojeDetalhes: [],
        procedimentosEnvolvido: 0,
        totalProcedimentos: procedimentos.length
      };
    }

    // Identificar o colaborador atual a partir da sessão (usar o já calculado)
    const userId = String(currentColaborador?.id || session.usuario.id || '');

    const allCards = flows.flatMap(f => f.cards || []);

    const flowsParticipa = flows.filter(flow => {
      return flow.etapas?.some(etapa => {
        const participantes = etapa.participantes?.map(p => String(p)) || [];
        return participantes.includes(String(userId));
      }) ?? false;
    });

    // Datas de vencimento apenas dos cards envolvidos
    let cardsVencidos = 0;
    let cardsVencendoHoje = 0;
    let cardsVencendo24h = 0;
    let cardsEmDia = 0;
    let totalCardsParticipa = 0; // NOVO: contador de todos os cards que o usuário participa
    const cardsVencidosDetalhes: CardVencidoDetalhes[] = [];
    const cardsVencendoHojeDetalhes: CardVencidoDetalhes[] = [];

  const agoraGlobal = new Date();

    // Analisar TODOS os cards dos fluxos verificando participação por etapa
    flows.forEach(flow => {
      flow.cards?.forEach(card => {
        const etapa = flow.etapas?.find(e => String(e.id) === String(card.etapaId));
        if (!etapa) return;
        
        // Verificar se usuário participa deste card:
        // 1. Se tem responsável individual, verificar se é o responsável
        // 2. Se não tem responsável individual, verificar se participa da etapa
        const userParticipa = card.responsavelIndividualId
          ? String(card.responsavelIndividualId) === String(userId)
          : (etapa.participantes?.map(p => String(p)) || []).includes(String(userId));
        
        // Debug de participação
        console.log(`Card "${card.titulo}" - Etapa "${etapa.nome}":`, {
          responsavelIndividual: card.responsavelIndividualId,
          participantesEtapa: etapa.participantes,
          userId,
          userParticipa
        });
        
        if (!userParticipa) return;

        // INCREMENTAR contador de cards que participa
        totalCardsParticipa++;

  // Buscar campo de vencimento exatamente como no Kanban (tipo === 'data_vencimento')
  console.log(`📋 Campos da etapa "${etapa.nome}":`, etapa.campos?.map(c => `${c.nome} (${c.tipo})`));
  const campoVencimento = etapa.campos?.find(campo => campo.tipo === 'data_vencimento');
  console.log(`📅 Campo de vencimento encontrado:`, campoVencimento);
        
        if (!campoVencimento) {
          console.log(`❌ Nenhum campo de vencimento encontrado para card "${card.titulo}"`);
          cardsEmDia++;
          return;
        }
        
        const dataVencimento = card.campos?.[campoVencimento.id];
        console.log(`Dados do card "${card.titulo}":`, {
          campoVencimentoId: campoVencimento.id,
          cardCampos: card.campos,
          dataVencimento
        });
        
        if (!dataVencimento) {
          console.log(`❌ Data de vencimento não encontrada para card "${card.titulo}"`);
          cardsEmDia++;
          return;
        }
        
        try {
          // Mesma lógica do Kanban: parse direto e comparação com "agora"
          const vencimento = new Date(dataVencimento as any);
          if (isNaN(vencimento.getTime())) {
            console.warn(`❌ Data inválida para card "${card.titulo}": ${dataVencimento}`);
            cardsEmDia++;
            return;
          }

          const agora = agoraGlobal;
          // Se já venceu
          if (vencimento < agora) {
            cardsVencidos++;
            const diasVencido = Math.floor((agora.getTime() - vencimento.getTime()) / (1000 * 60 * 60 * 24));
            cardsVencidosDetalhes.push({
              id: card.id,
              titulo: card.titulo,
              etapaNome: etapa.nome,
              flowNome: flow.name || 'Fluxo não encontrado',
              dataVencimento: vencimento.toLocaleDateString('pt-BR'),
              diasVencido
            });
            console.log(`🔴 CARD VENCIDO DETECTADO: "${card.titulo}" - ${diasVencido} dias vencido`);
          } else {
            // Se vence em até 24 horas
            const diferencaHoras = (vencimento.getTime() - agora.getTime()) / (1000 * 60 * 60);
            if (diferencaHoras <= 24) {
              cardsVencendoHoje++;
              cardsVencendoHojeDetalhes.push({
                id: card.id,
                titulo: card.titulo,
                etapaNome: etapa.nome,
                flowNome: flow.name || 'Fluxo não encontrado',
                dataVencimento: vencimento.toLocaleDateString('pt-BR'),
                venceHoje: true
              });
              console.log(`🟡 CARD VENCE EM ATÉ 24H: "${card.titulo}"`);
            } else if (diferencaHoras <= 48) {
              // Mantém cardsVencendo24h como indicador adicional
              cardsVencendo24h++;
            } else {
              cardsEmDia++;
            }
          }
        } catch (error) {
          console.warn('Erro ao processar data de vencimento:', error);
          cardsEmDia++;
        }
      });
    });

    console.log(`📊 RESUMO FINAL DE PROCESSAMENTO:`, {
      cardsVencidos,
      cardsVencendoHoje,
      cardsVencendo24h,
      cardsEmDia,
      detalhesVencidos: cardsVencidosDetalhes,
      totalProcessado: cardsVencidos + cardsVencendoHoje + cardsVencendo24h + cardsEmDia
    });

    // Calcular procedimentos que o colaborador está envolvido
    const procedimentosEnvolvido = procedimentos.filter(proc => 
      proc.colaboradores && proc.colaboradores.includes(userId)
    ).length;

    const result = {
      totalCards: allCards.length,
      cardsParticipa: totalCardsParticipa, // CORRIGIDO: usar o contador total
      cardsVencidos,
      cardsVencendoHoje,
      cardsEmDia,
      cardsVencendo24h,
      flowsParticipa: flowsParticipa.length,
      totalFlows: flows.length,
      cardsVencidosDetalhes,
      cardsVencendoHojeDetalhes,
      procedimentosEnvolvido,
      totalProcedimentos: procedimentos.length
    };
    
    console.log('=== CALCULATED METRICS ===');
    console.log('Total cards:', result.totalCards);
    console.log('Cards participa:', result.cardsParticipa);
    console.log('Cards vencidos:', result.cardsVencidos);
    console.log('Cards vencendo hoje:', result.cardsVencendoHoje);
    console.log('Cards em dia:', result.cardsEmDia);
    console.log('Cards vencendo 24h:', result.cardsVencendo24h);
    console.log('Fluxos participa:', result.flowsParticipa);
    console.log('Total flows:', result.totalFlows);
    console.log('==============================');
    
    return result;
  }, [flows, session, procedimentos]);

  // Calcular distribuição de cards por fluxo para mini-dashboard
  const flowDistribution = useMemo(() => {
    return flows
      .map(flow => ({
        name: flow.name || 'Sem nome',
        cards: flow.cards?.length || 0,
        etapas: flow.etapas?.length || 0
      }))
      .filter(f => f.cards > 0)
      .sort((a, b) => b.cards - a.cards)
      .slice(0, 5);
  }, [flows]);

  // Calcular cards por etapa do fluxo mais ativo
  const topFlowEtapas = useMemo(() => {
    if (flowDistribution.length === 0) return [];
    const topFlow = flows.find(f => f.name === flowDistribution[0]?.name);
    if (!topFlow?.etapas) return [];
    
    return topFlow.etapas.map(etapa => ({
      nome: etapa.nome,
      cards: topFlow.cards?.filter(c => String(c.etapaId) === String(etapa.id)).length || 0
    })).filter(e => e.cards > 0);
  }, [flows, flowDistribution]);

  // Atividades recentes - últimos cards modificados
  const recentActivity = useMemo(() => {
    const allCards = flows.flatMap(flow => 
      (flow.cards || []).map(card => ({
        ...card,
        flowName: flow.name,
        flowId: flow.id,
        etapaNome: flow.etapas?.find(e => String(e.id) === String(card.etapaId))?.nome || 'Etapa'
      }))
    );
    
    return allCards
      .filter(card => card.updatedAt || card.createdAt)
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, 5);
  }, [flows]);

  // Calcular percentuais para gráfico de rosca
  const statusPercentages = useMemo(() => {
    const total = userMetrics.cardsParticipa;
    if (total === 0) return { emDia: 0, vencendo: 0, vencidos: 0 };
    return {
      emDia: Math.round((userMetrics.cardsEmDia / total) * 100),
      vencendo: Math.round((userMetrics.cardsVencendoHoje / total) * 100),
      vencidos: Math.round((userMetrics.cardsVencidos / total) * 100)
    };
  }, [userMetrics]);

  const renderCardsVencidosPopover = (
    cardsDetalhes: CardVencidoDetalhes[],
    titulo: string,
    targetRef: RefObject<HTMLElement>
  ) => {
    if (!targetRef.current || cardsDetalhes.length === 0) return null;

    const rect = targetRef.current.getBoundingClientRect();
    const isVencidos = titulo.includes('vencidos') || titulo.includes('Vencidos');

    return (
      <div 
        className="fixed z-50 bg-white rounded-xl shadow-xl border border-slate-200 p-5 min-w-80 max-w-96"
        style={{
          top: rect.bottom + 12,
          left: Math.max(16, rect.left - 160),
          maxHeight: '400px',
          overflowY: 'auto'
        }}
      >
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-100">
          <div className={`w-3 h-3 rounded-full ${isVencidos ? 'bg-red-500' : 'bg-amber-500'}`} />
          <h4 className="font-semibold text-slate-800 text-sm">{titulo}</h4>
        </div>
        <div className="space-y-3">
          {cardsDetalhes.map((card, index) => (
            <div 
              key={`${card.id}-${index}`} 
              className="group bg-slate-50 rounded-lg p-3 border-l-2 border-slate-200 hover:border-blue-500 transition-all cursor-pointer hover:bg-slate-100"
              onClick={() => {
                if (onCardClick) {
                  const flow = flows.find(f => f.cards?.some((c: any) => String(c.id) === String(card.id)));
                  if (flow) {
                    onCardClick(String(flow.id), String(card.id));
                    setShowingCardsDetalhes(null);
                  }
                }
              }}
            >
              <div className="font-medium text-slate-800 text-sm mb-2 group-hover:text-blue-600 transition-colors">{card.titulo}</div>
              <div className="text-xs text-slate-500 space-y-1">
                <div><span className="font-medium">Fluxo:</span> {card.flowNome}</div>
                <div><span className="font-medium">Etapa:</span> {card.etapaNome}</div>
                <div><span className="font-medium">Vencimento:</span> {card.dataVencimento}</div>
                {card.diasVencido !== undefined && (
                  <div className="text-red-600 font-medium bg-red-50 rounded px-2 py-1 mt-2 text-xs">
                    ⚠️ {card.diasVencido} dia{card.diasVencido !== 1 ? 's' : ''} em atraso
                  </div>
                )}
                {card.venceHoje && (
                  <div className="text-amber-600 font-medium bg-amber-50 rounded px-2 py-1 mt-2 text-xs">
                    ⏰ Vence hoje
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-white to-purple-50">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-5">
        
        {/* Header Simples */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 px-5 py-4 mb-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm overflow-hidden">
              {currentColaborador?.foto ? (
                <img src={currentColaborador.foto} alt="Foto" className="w-full h-full object-cover" />
              ) : (
                session.usuario.nome?.charAt(0)?.toUpperCase() || 'U'
              )}
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">
                {session.usuario.nome?.split(' ')[0]}
              </h1>
              <p className="text-xs text-gray-500">{session.empresa?.nome || 'Empresa'}</p>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          {/* Meus Cards */}
          <div 
            className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 p-4 cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all"
            onClick={() => onNavigate?.('/fluxos')}
          >
            <p className="text-xs text-gray-500 font-medium mb-1">Meus Cards</p>
            <p className="text-2xl font-bold text-indigo-600">{userMetrics.cardsParticipa}</p>
          </div>

          {/* Atrasados */}
          <div 
            className={`bg-white/80 backdrop-blur-md rounded-xl border p-4 cursor-pointer hover:shadow-md transition-all ${userMetrics.cardsVencidos > 0 ? 'border-red-200 hover:border-red-300' : 'border-gray-200/50'}`}
            onClick={() => userMetrics.cardsVencidos > 0 && setShowingCardsDetalhes({ tipo: 'vencidos', cards: userMetrics.cardsVencidosDetalhes })}
          >
            <p className="text-xs text-gray-500 font-medium mb-1">Atrasados</p>
            <p ref={vencidosRef} className={`text-2xl font-bold ${userMetrics.cardsVencidos > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {userMetrics.cardsVencidos}
            </p>
            {userMetrics.cardsVencidos > 0 && (
              <p className="text-red-500 text-[10px] mt-1">Requer atenção</p>
            )}
          </div>

          {/* Vencem Hoje */}
          <div 
            className={`bg-white/80 backdrop-blur-md rounded-xl border p-4 cursor-pointer hover:shadow-md transition-all ${userMetrics.cardsVencendoHoje > 0 ? 'border-amber-200 hover:border-amber-300' : 'border-gray-200/50'}`}
            onClick={() => userMetrics.cardsVencendoHoje > 0 && setShowingCardsDetalhes({ tipo: 'vencendoHoje', cards: userMetrics.cardsVencendoHojeDetalhes })}
          >
            <p className="text-xs text-gray-500 font-medium mb-1">Vencem Hoje</p>
            <p ref={vencendoHojeRef} className={`text-2xl font-bold ${userMetrics.cardsVencendoHoje > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
              {userMetrics.cardsVencendoHoje}
            </p>
          </div>

          {/* Em Dia */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 p-4 cursor-pointer hover:shadow-md hover:border-emerald-200 transition-all">
            <p className="text-xs text-gray-500 font-medium mb-1">Em Dia</p>
            <p className="text-2xl font-bold text-emerald-600">{userMetrics.cardsEmDia}</p>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-5">
            
            {/* Cards por Fluxo */}
            <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 shadow-sm">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">Cards por Fluxo</h2>
                <button 
                  onClick={() => onNavigate?.('/dashboards')}
                  className="text-xs text-indigo-600 font-medium hover:text-indigo-700"
                >
                  Ver Análises →
                </button>
              </div>
              
              <div className="divide-y divide-gray-100">
                {flowDistribution.length > 0 ? (
                  flowDistribution.map((flow, idx) => (
                    <div key={idx} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50/50 transition-colors">
                      <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 font-medium truncate">{flow.name}</p>
                        <p className="text-[10px] text-gray-500">{flow.etapas} etapas</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{flow.cards}</p>
                        <p className="text-[10px] text-gray-500">cards</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-8 text-center text-gray-400 text-sm">
                    Nenhum fluxo com cards
                  </div>
                )}
              </div>
            </div>

            {/* Etapas do Fluxo Principal */}
            {topFlowEtapas.length > 0 && (
              <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 shadow-sm">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h2 className="text-sm font-semibold text-gray-900">Etapas - {flowDistribution[0]?.name}</h2>
                </div>
                
                <div className="divide-y divide-gray-100">
                  {topFlowEtapas.slice(0, 8).map((etapa, idx) => (
                    <div key={idx} className="px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50/50 transition-colors">
                      <span className="w-5 h-5 bg-gray-100 rounded text-gray-600 text-xs font-medium flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <p className="flex-1 text-sm text-gray-700 truncate">{etapa.nome}</p>
                      <span className="text-sm font-semibold text-gray-900">{etapa.cards}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            
            {/* Resumo */}
            <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 shadow-sm">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-900">Resumo</h2>
              </div>
              
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
                  <span className="text-xs text-gray-500">Fluxos Totais</span>
                  <span className="text-sm font-semibold text-gray-900">{userMetrics.totalFlows}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
                  <span className="text-xs text-gray-500">Meus Fluxos</span>
                  <span className="text-sm font-semibold text-indigo-600">{userMetrics.flowsParticipa}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
                  <span className="text-xs text-gray-500">Cards Totais</span>
                  <span className="text-sm font-semibold text-gray-900">{userMetrics.totalCards}</span>
                </div>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-gray-500">Procedimentos</span>
                  <span className="text-sm font-semibold text-gray-900">{userMetrics.procedimentosEnvolvido}</span>
                </div>
              </div>
            </div>

            {/* Status */}
            {userMetrics.cardsParticipa > 0 && (
              <div className="bg-white/80 backdrop-blur-md rounded-xl border border-gray-200/50 shadow-sm">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h2 className="text-sm font-semibold text-gray-900">Status</h2>
                </div>
                
                <div className="p-4 space-y-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                    <span className="flex-1 text-xs text-gray-600">Em dia</span>
                    <span className="text-sm font-semibold text-gray-900">{userMetrics.cardsEmDia}</span>
                    <span className="text-[10px] text-gray-400 w-10 text-right">{statusPercentages.emDia}%</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                    <span className="flex-1 text-xs text-gray-600">Vencendo</span>
                    <span className="text-sm font-semibold text-gray-900">{userMetrics.cardsVencendoHoje}</span>
                    <span className="text-[10px] text-gray-400 w-10 text-right">{statusPercentages.vencendo}%</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                    <span className="flex-1 text-xs text-gray-600">Atrasados</span>
                    <span className="text-sm font-semibold text-gray-900">{userMetrics.cardsVencidos}</span>
                    <span className="text-[10px] text-gray-400 w-10 text-right">{statusPercentages.vencidos}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Ações */}
            <div className="space-y-2">
              <button
                onClick={() => onNavigate?.('/dashboards')}
                className="w-full bg-gradient-to-r from-gray-900 to-black text-white rounded-lg px-4 py-3 flex items-center justify-between hover:opacity-90 transition-opacity shadow-md"
              >
                <div className="flex items-center gap-2.5">
                  <ChartBarIcon className="h-4 w-4" />
                  <span className="text-sm font-medium">Dashboards</span>
                </div>
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </button>
              
              <button
                onClick={() => onNavigate?.('/fluxos')}
                className="w-full bg-white/80 backdrop-blur-md border border-gray-200/50 text-gray-700 rounded-lg px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  <Squares2X2Icon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium">Meus Fluxos</span>
                </div>
                <ArrowRightIcon className="h-3.5 w-3.5 text-gray-400" />
              </button>
            </div>

            {/* Sucesso */}
            {userMetrics.cardsVencidos === 0 && userMetrics.cardsVencendoHoje === 0 && userMetrics.cardsParticipa > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-start gap-2.5">
                  <CheckCircleIcon className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Tudo em dia</p>
                    <p className="text-xs text-emerald-600 mt-0.5">Nenhum card atrasado ou vencendo hoje.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Popover */}
        {showingCardsDetalhes && (
          <>
            <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setShowingCardsDetalhes(null)} />
            {renderCardsVencidosPopover(
              showingCardsDetalhes.cards,
              showingCardsDetalhes.tipo === 'vencidos' 
                ? `${showingCardsDetalhes.cards.length} Cards Atrasados`
                : `${showingCardsDetalhes.cards.length} Cards Vencendo Hoje`,
              showingCardsDetalhes.tipo === 'vencidos' ? vencidosRef : vencendoHojeRef
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Home;
